/**
 * ReservaFinancieroModal — Modal centrado enfocado en la vista FINANCIERA
 * de una reserva. Se usa desde el diálogo CxC de agencia (y a futuro desde
 * cualquier flujo de cobranza).
 *
 * NO reemplaza el ReservaSlideOver de Booking (que sirve para operar la
 * reserva). Este es solo LECTURA + AUDITORÍA de movimientos financieros.
 *
 * Consume: GET /turismo/reservaciones/{id}/movimientos
 *   - Trae desglose financiero, totales, alertas y lista unificada de
 *     movimientos (comisiones, vouchers, cobros de muelle, descuentos).
 */
import React, { useEffect, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Chip, Button, CircularProgress, Alert, IconButton, Tooltip,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';
const fmtMoney = (n) => `$${Number(n || 0).toLocaleString('es-CO')}`;
const fmtFecha = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
};
const fmtFechaHora = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('es-CO', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
};

// Colores por tipo de movimiento (para el chip)
const TIPO_COLOR = {
  cxc_agencia:        { bg: '#fef2f2', fg: '#7f1d1d', label: 'CxC agencia' },
  voucher:            { bg: '#dbeafe', fg: '#1e40af', label: 'Voucher' },
  pago_contable:      { bg: '#ecfdf5', fg: '#065f46', label: 'Pago' },
  liquidacion:        { bg: '#fefce8', fg: '#78350f', label: 'Liquidación' },
  muelle:             { bg: '#f3e8ff', fg: '#6b21a8', label: 'Muelle' },
  descuento:          { bg: '#e0f2fe', fg: '#075985', label: 'Descuento' },
  comision_anticipada:{ bg: '#fefce8', fg: '#78350f', label: 'Comisión ant.' },
};

const ESTADO_COLOR = {
  aprobado:  '#059669',
  aplicado:  '#059669',
  liquidada: '#059669',
  pendiente: '#d97706',
  rechazado: '#dc2626',
  revertido: '#dc2626',
};

// A quién se le cobra el saldo. Son dos cobranzas distintas y hasta ahora
// solo se distinguían leyendo la frase del resumen: MUELLE se cobra en el
// embarcadero el día del pasadía; CxC queda contra el cupo de la agencia y
// lo persigue cartera. Confundirlas manda a alguien a cobrar donde no es.
const CARGO_BADGE = {
  muelle:  { label: 'COBRO EN MUELLE',   bg: '#fef3c7', fg: '#92400e', bd: '#fcd34d' },
  agencia: { label: 'CUENTA POR COBRAR', bg: '#e0e7ff', fg: '#3730a3', bd: '#a5b4fc' },
};
// Cuando el backend no puede afirmar el canal de cobro, el badge igual sale:
// el monto pendiente es cierto aunque el "dónde" no se sepa. Antes, sin canal
// no había badge y la reserva se veía como si no debiera nada.
const CARGO_NEUTRO = { label: 'SALDO POR COBRAR', bg: '#f1f5f9', fg: '#475569', bd: '#cbd5e1' };

export default function ReservaFinancieroModal({ reservaId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!reservaId) return;
    setLoading(true);
    setError('');
    fetch(`${API_BASE}/turismo/reservaciones/${reservaId}/movimientos`)
      .then(r => { if (!r.ok) throw new Error('No se pudo cargar'); return r.json(); })
      .then(setData)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, [reservaId]);

  const d = data || {};
  const dg = d.desglose_financiero || {};
  const totales = d.totales || {};

  // KPIs: precio · comisión · cobrado · pendiente
  const cobradoReal = (totales.voucher_aprobado || 0)
    + (totales.cobros_muelle || 0)
    + (totales.comision_anticipada || 0);
  const pendiente = d.saldo_pendiente ?? 0;

  // Líneas del precio, en el orden en que se cobran. Sólo entran las
  // categorías con pasajeros: un "0 bebés × $0" no explica nada.
  // Los charters no aparecen: se cotizan por embarcación, no por cabeza, y
  // su desglose viene en ceros — inventarle un precio unitario sería falso.
  const ETIQUETAS = [
    ['adultos', 'adulto', 'adultos'],
    ['ninos',   'niño',   'niños'],
    ['bebes',   'bebé',   'bebés'],
  ];
  const lineasPrecio = (d.tipo_reservacion === 'alquiler' ? [] : ETIQUETAS)
    .map(([clave, sing, plur]) => {
      const x = (d.desglose_precio || {})[clave] || {};
      const cantidad = Number(x.cantidad || 0);
      const unitario = Number(x.precio_unitario || 0);
      const subtotal = Number(x.subtotal || 0);
      return { clave, cantidad, unitario, subtotal,
               etiqueta: cantidad === 1 ? sing : plur };
    })
    .filter(l => l.cantidad > 0 && l.subtotal > 0);

  // Diferencia entre lo que suma el desglose y lo que se cobró de verdad.
  const sumaLineas = lineasPrecio.reduce((a, l) => a + l.subtotal, 0);
  const ajustePrecio = lineasPrecio.length
    ? Math.round((dg.precio_cliente || 0) - sumaLineas)
    : 0;

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}>
      <DialogTitle sx={{
        background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)',
        color: '#fff', p: 2, display: 'flex', alignItems: 'center', gap: 1.5,
      }}>
        <Box sx={{
          width: 40, height: 40, borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.15)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <ConfirmationNumberIcon />
        </Box>
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 15, fontWeight: 800 }}>
            {d.codigo_reserva || 'Cargando…'}
          </Typography>
          <Typography sx={{ fontSize: 12, opacity: 0.85 }}>
            Vista financiera · solo lectura
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: '#fff' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2 }}>
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && error && (
          <Alert severity="error">{error}</Alert>
        )}

        {!loading && data && (
          <>
            {/* Header con identidad de la reserva */}
            <Box sx={{ mb: 2, p: 1.5, bgcolor: '#f8fafc', borderRadius: 2, border: '1px solid #e2e8f0' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: 15, fontWeight: 700, color: '#0f172a' }}>
                  {d.nombre_cliente || '(sin nombre)'}
                </Typography>
                {/* El código NO se repite acá: ya lo lleva la barra de título
                    del modal, justo encima. */}
                {/* El badge solo aparece si queda saldo por cobrar: en una
                    reserva ya pagada no hay a quién cobrarle, y anunciarlo
                    ahí sería ruido. */}
                {pendiente > 0 && (() => {
                  // El monto va DENTRO del badge, no aparte: "cobro en muelle"
                  // sin cifra obliga a bajar la vista hasta el KPI de saldo
                  // para saber cuánto hay que cobrar. La pregunta es una sola
                  // —a quién y cuánto— y se responde de un golpe.
                  const muelle = Number(d.saldo_muelle_monto || 0);
                  const cxc = Number(d.cxc_agencia_monto || 0);

                  // Saldo PARTIDO: parte se cobra en el embarcadero y parte
                  // queda contra el cupo de la agencia. Van dos badges, cada
                  // uno con SU cifra. Mostrar el pendiente completo en el de
                  // muelle manda a cobrarle al turista lo que debe la agencia.
                  const partido = muelle > 0 && cxc > 0;
                  const trozos = partido
                    ? [[CARGO_BADGE.muelle, muelle], [CARGO_BADGE.agencia, cxc]]
                    : [[CARGO_BADGE[d.saldo_cobro_en] || CARGO_NEUTRO, pendiente]];

                  return trozos.map(([bg, monto]) => (
                  <Box key={bg.label}
                       sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.75,
                              px: 1, py: 0.3, borderRadius: 1,
                              bgcolor: bg.bg, color: bg.fg,
                              border: `1px solid ${bg.bd}` }}>
                    <Box component="span" sx={{ fontSize: 10, fontWeight: 800, letterSpacing: .4 }}>
                      {bg.label}
                    </Box>
                    <Box component="span" sx={{ fontSize: 12.5, fontWeight: 800,
                                                 fontVariantNumeric: 'tabular-nums' }}>
                      {fmtMoney(monto)}
                    </Box>
                  </Box>
                  ));
                })()}
              </Box>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 0.5, fontSize: 12, color: '#64748b' }}>
                {d.paquete_nombre && <span>🏝️ {d.paquete_nombre}</span>}
                {d.fecha_salida && <span>📅 {fmtFecha(d.fecha_salida)}</span>}
                {(d.adultos || d.ninos || d.bebes) != null && (
                  <span>
                    👥 {d.adultos || 0}A · {d.ninos || 0}N · {d.bebes || 0}B
                    {d.cortesias > 0 ? ` · ${d.cortesias} cortesía${d.cortesias > 1 ? 's' : ''}` : ''}
                  </span>
                )}
                {dg.agencia_nombre && <span>🏢 {dg.agencia_nombre}</span>}
              </Box>
              {d.resumen?.texto && (
                <Alert severity={d.resumen.color || 'info'} sx={{ mt: 1, fontSize: 12, py: 0.5 }}>
                  {d.resumen.texto}
                </Alert>
              )}
            </Box>

            {/* KPIs financieros */}
            <Box sx={{ display: 'grid',
                       gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                       gap: 1, mb: 2 }}>
              <KpiCell label="Precio total"   value={fmtMoney(dg.precio_total)}    color="#0f172a" />
              <KpiCell label="Comisión"        value={fmtMoney(dg.comision_efectiva)} color="#78350f" />
              <KpiCell label="Cobrado"         value={fmtMoney(cobradoReal)}         color="#059669" />
              <KpiCell label="Saldo pendiente" value={fmtMoney(pendiente)}
                       color={pendiente > 0 ? '#dc2626' : '#059669'} highlight />
            </Box>

            {/* Reparto del dinero — a quién le pertenece cada peso de la venta.
                El KPI de arriba dice "Comisión $280.000" y ahí se acaba; no
                dice que $80.000 ya se los quedó la agencia reteniéndolos al
                cliente y que $200.000 salen del bolsillo de Entretur más
                adelante. Sin eso, alguien lee el ingreso de $640.000 como si
                fuera todo suyo. */}
            {dg.comision_total > 0 && (
              <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, border: '1px solid #e2e8f0', bgcolor: '#fff' }}>
                <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#64748b',
                                   textTransform: 'uppercase', letterSpacing: .5, mb: 1 }}>
                  Reparto del dinero
                </Typography>

                {/* Cómo se armó el precio. Sale de `desglose_precio`, que guarda
                    el precio unitario APLICADO AL RESERVAR — no el del paquete
                    hoy. Los precios cambian: dos reservas de la misma salida
                    tienen el niño a $300.000 y a $310.000. Recalcular contra el
                    parámetro actual daría un desglose que no suma lo que el
                    cliente pagó. */}
                {lineasPrecio.map(l => (
                  <FilaReparto key={l.clave}
                    label={`${l.cantidad} ${l.etiqueta} × ${fmtMoney(l.unitario)}`}
                    valor={l.subtotal} color="#475569" sangria peso={600} />
                ))}

                {/* Si el desglose no suma el precio, la diferencia se muestra.
                    Callarla dejaría una cuenta que no cierra a la vista del
                    contador — hoy pasa en 5 pasadías con precio ajustado a
                    mano. Mejor que se vea a que no cuadre en silencio. */}
                {ajustePrecio !== 0 && (
                  <FilaReparto label="Ajuste sobre el precio de lista"
                    nota="El desglose por pasajero no suma el precio cobrado"
                    valor={ajustePrecio} color="#7c3aed" sangria />
                )}

                <FilaReparto label="Venta al cliente" valor={dg.precio_cliente}
                             color="#0f172a" peso={800}
                             borde={lineasPrecio.length > 0} />

                {dg.comision_anticipada > 0 && (
                  <FilaReparto
                    label={`Retenido por ${dg.agencia_nombre || 'la agencia'} (anticipo)`}
                    nota="Nunca entró a Entretur — la agencia lo descontó al cobrarle al cliente"
                    valor={-dg.comision_anticipada} color="#92400e" sangria />
                )}

                <FilaReparto label="Entra a Entretur"
                             valor={dg.precio_cliente - dg.comision_anticipada}
                             color="#0f172a" peso={800} borde />

                {dg.comision_pendiente > 0 && (
                  <FilaReparto
                    label={`De ${dg.agencia_nombre || 'la agencia'} — pendiente de girar`}
                    nota="Entretur lo tiene en caja, pero es de la agencia"
                    valor={dg.comision_pendiente} color="#b45309" sangria />
                )}

                <FilaReparto label="Neto de Entretur"
                             nota="Lo que realmente le queda al operador"
                             valor={dg.neto_entretur} color="#15803d" sangria peso={800} />
              </Box>
            )}

            {/* Alertas */}
            {(d.alertas || []).length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                {d.alertas.map(a => (
                  <Alert key={a.id} severity={a.severity || 'info'} sx={{ fontSize: 12, py: 0.5 }}>
                    {a.texto}
                  </Alert>
                ))}
              </Box>
            )}

            {/* Extracto de movimientos */}
            <Typography sx={{ fontSize: 11, fontWeight: 800, color: '#64748b',
                              textTransform: 'uppercase', letterSpacing: 0.5, mb: 0.5 }}>
              📋 Movimientos (extracto)
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 320 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow sx={{ '& th': { bgcolor: '#f8fafc', fontWeight: 700, fontSize: 11 } }}>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell align="center">Estado</TableCell>
                    <TableCell align="center">Soporte</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(d.movimientos || []).length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ color: '#94a3b8', fontSize: 12, py: 3 }}>
                        Esta reserva aún no tiene movimientos financieros registrados.
                      </TableCell>
                    </TableRow>
                  )}
                  {(d.movimientos || []).map((m, i) => {
                    const tc = TIPO_COLOR[m.tipo] || TIPO_COLOR.pago_contable;
                    const ec = ESTADO_COLOR[m.estado] || '#64748b';
                    // Signo: los cobros suman, la CxC/descuentos aparecen como "-" solo visual
                    const esNegativo = m.tipo === 'cxc_agencia' || m.tipo === 'descuento';
                    // El endpoint pone `imagen_url` en el movimiento mismo; antes
                    // sólo se buscaba dentro de `metadata` y por eso el icono de
                    // comprobante NUNCA aparecía, aunque el voucher existiera.
                    const soporteUrl = m.imagen_url || m.comprobante_url
                      || m.metadata?.comprobante_url || m.metadata?.imagen_url;
                    return (
                      <TableRow key={i} hover>
                        <TableCell sx={{ fontSize: 11, whiteSpace: 'nowrap' }}>
                          {fmtFechaHora(m.fecha)}
                        </TableCell>
                        <TableCell>
                          <Chip size="small" label={tc.label}
                            sx={{ bgcolor: tc.bg, color: tc.fg,
                                  fontSize: 10, fontWeight: 700, height: 20 }} />
                          {m.subtipo && (
                            <Typography sx={{ fontSize: 9, color: '#94a3b8', mt: 0.25 }}>
                              {m.subtipo}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: 11 }}>
                          {m.descripcion}
                          {m.notas && (
                            <Typography sx={{ fontSize: 10, color: '#64748b', fontStyle: 'italic', mt: 0.25 }}>
                              {m.notas}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{
                          fontSize: 12, fontWeight: 700,
                          color: esNegativo ? '#94a3b8' : '#0f172a',
                        }}>
                          {esNegativo ? '' : '+ '}{fmtMoney(m.monto)}
                        </TableCell>
                        <TableCell align="center">
                          <Chip size="small" label={m.estado}
                            sx={{ bgcolor: `${ec}18`, color: ec,
                                  fontSize: 10, fontWeight: 700, height: 20 }} />
                        </TableCell>
                        <TableCell align="center">
                          {soporteUrl ? (
                            <Tooltip title="Ver comprobante">
                              <IconButton size="small" component="a"
                                href={soporteUrl.startsWith('http')
                                  ? soporteUrl
                                  : `${API_BASE.replace(/\/api$/, '')}${soporteUrl}`}
                                target="_blank" rel="noopener"
                                sx={{ color: '#0891b2' }}>
                                <ReceiptLongIcon sx={{ fontSize: 16 }} />
                              </IconButton>
                            </Tooltip>
                          ) : (
                            <Typography sx={{ fontSize: 11, color: '#cbd5e1' }}>—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Footer semántico */}
            <Box sx={{ mt: 1, textAlign: 'right' }}>
              <Typography sx={{ fontSize: 10, color: '#94a3b8', fontStyle: 'italic' }}>
                Vista solo lectura · para editar la reserva usa el módulo Booking.
              </Typography>
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, pt: 0 }}>
        <Button onClick={onClose} variant="contained"
          sx={{ textTransform: 'none', fontWeight: 700,
                background: 'linear-gradient(135deg, #0891b2 0%, #0e7490 100%)' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Una línea del reparto. `sangria` la desplaza como sub-nivel; `borde` la
// separa como subtotal. Un valor negativo se pinta con su signo para que se
// lea como lo que es: plata que se va antes de llegar.
function FilaReparto({ label, nota, valor, color, sangria, peso = 600, borde }) {
  return (
    <Box sx={{
      display: 'flex', alignItems: 'baseline', gap: 1, py: 0.6,
      pl: sangria ? 2 : 0,
      borderTop: borde ? '1px solid #e2e8f0' : 'none',
      mt: borde ? 0.4 : 0,
    }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 12.5, fontWeight: peso, color }}>
          {sangria ? '└ ' : ''}{label}
        </Typography>
        {nota && (
          <Typography sx={{ fontSize: 10.5, color: '#94a3b8', fontStyle: 'italic' }}>
            {nota}
          </Typography>
        )}
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: peso === 800 ? 800 : 700, color,
                         fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
        {valor < 0 ? `− ${fmtMoney(Math.abs(valor))}` : fmtMoney(valor)}
      </Typography>
    </Box>
  );
}

function KpiCell({ label, value, color, highlight }) {
  return (
    <Box sx={{ p: 1.25, borderRadius: 2,
               bgcolor: highlight ? `${color}10` : '#f8fafc',
               border: highlight ? `2px solid ${color}30` : '1px solid #e2e8f0' }}>
      <Typography sx={{ fontSize: 9.5, color: '#64748b', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 15, fontWeight: 800, color, mt: 0.25 }}>
        {value}
      </Typography>
    </Box>
  );
}
